import styled from "@emotion/styled";
import { Global, Theme } from "@emotion/react";
import { H1, H2, SiteContainer, Table, Text } from "atoms";
import { Head } from "features/misc";

function PrivacyPolicy() {
  return (
    <Main>
      <Head title="Privacy Policy" />

      <Global styles={global} />
      <SiteContainer>
        <Header>
          <H1>The HUG Privacy Policy</H1>
          <SText>Effective Date: May 31, 2022</SText>
        </Header>
        <Section>
          <SText>
            This Privacy Policy describes the privacy practices of The HUG
            (&#8220;<strong>The HUG,</strong>&#8221; &#8220;<strong>us,</strong>
            &#8221; &#8220;<strong>we,</strong>&#8221; or &#8220;
            <strong>our</strong>&#8221;), which is owned by Assemble Stream,
            Inc. This Privacy Policy describes how we collect, use, and share
            personal information. It applies to your use of our website
            (thehug.xyz), our other online platforms that link to this Policy,
            and to other interactions you have with us (together, our &#8220;
            <strong>Service</strong>&#8221;).
          </SText>

          <SText>
            <strong>
              Please read our Privacy Policy carefully before using the Service.
              By using the Service or otherwise providing personal information
              to us, you agree to our privacy practices as described in this
              Privacy Policy.
            </strong>
          </SText>
        </Section>

        <Section>
          <H2>Contents</H2>
          <UL>
            <LI>Personal Information We Collect and Why We Collect It</LI>
            <LI>
              Technologies that Automatically Collect Information About You
            </LI>
            <LI>How We Share Personal Information</LI>
            <LI>International Visitors</LI>
            <LI>Your Choices About Email Communications From Us</LI>
            <LI>Security and Passwords</LI>
            <LI>Age Restrictions</LI>
            <LI>Changes to This Policy</LI>
            <LI>How to Contact Us</LI>
          </UL>
        </Section>

        <Section>
          <H2>Personal Information We Collect And Why We Collect It</H2>
          <SText>
            Personal information is information that identifies, relates to,
            describes, is reasonably capable of being associated with, or could
            reasonably be linked, directly or indirectly, to you. Personal
            information does not include information that is publicly available,
            de-identified, or aggregated.
          </SText>
          <SText>
            The categories of personal information we collect are shown in the
            chart below. Note that the specific pieces of personal information
            we collect about you may vary depending on the nature of your
            interactions with us, and may not include all of the examples
            listed.
          </SText>
          <Table scroll>
            <thead>
              <tr>
                <th>Category Of Personal Information</th>
                <th>Categories Of Sources From Which Collected</th>
                <th>Purposes For Collection, Use, And Disclosure</th>
                <th>Categories Of Third Parties To Which Disclosed</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <TD>
                  <SText>
                    <strong>Identifiers</strong> such as a real name, alias,
                    postal address, unique personal identifier, online
                    identifier (e.g., a digital wallet public key or mobile
                    device identifier), IP address, email address, account name,
                    Twitter handle, Discord account ID, or other similar
                    identifiers
                  </SText>
                </TD>
                <TD>
                  <SText>Directly from you</SText>
                  <SText>Technologies running on our Service </SText>
                  <SText>Other users of our Service</SText>
                  <SText>Affiliated companies within our corporate group</SText>
                  <SText>Our service providers</SText>
                </TD>
                <TD>
                  <SText>
                    To allow you to participate in the features we offer on the
                    Service
                  </SText>
                  <SText>To communicate with you</SText>
                  <SText>Marketing and advertising</SText>
                  <SText>
                    Customer service, auditing, research and analysis,
                    maintaining and improving the Service, and other internal
                    business purposes{" "}
                  </SText>
                  <SText>
                    To detect and prevent fraud against you and/or us
                  </SText>
                </TD>
                <TD>
                  <SText>Affiliated companies within our corporate group</SText>
                  <SText>
                    Service providers that help us communicate with you, provide
                    analytics, perform marketing or advertising services, or
                    otherwise help run our business
                  </SText>
                  <SText>Third-party services, such as social networks</SText>
                </TD>
              </tr>

              <tr>
                <TD>
                  <SText>
                    <strong>Personal records</strong> such as name, address,
                    telephone number
                  </SText>
                </TD>
                <TD>
                  <SText>Directly from you</SText>
                  <SText>Technologies running on our Service</SText>
                  <SText>Other users of our Service</SText>
                  <SText>Affiliated companies within our corporate group</SText>
                  <SText>Our service providers</SText>
                </TD>
                <TD>
                  <SText>
                    To allow you to participate in the features we offer on the
                    Service
                  </SText>
                  <SText>To communicate with you</SText>
                  <SText>Marketing and advertising</SText>
                  <SText>
                    Customer service, auditing, research and analysis,
                    maintaining and improving the Service, and other internal
                    business purposes
                  </SText>
                  <SText>
                    To detect and prevent fraud against you and/or us
                  </SText>
                </TD>
                <TD>
                  <SText>Affiliated companies within our corporate group</SText>
                  <SText>
                    Service providers that help us communicate with you, provide
                    analytics, perform marketing or advertising services, or
                    otherwise help run our business
                  </SText>
                  <SText>Third-party services, such as social networks</SText>
                </TD>
              </tr>

              <tr>
                <TD>
                  <SText>
                    <strong>Commercial information</strong>, including records
                    of personal property, products or services purchased,
                    obtained, or considered, or other purchasing or consuming
                    histories or tendencies
                  </SText>
                </TD>
                <TD>
                  <SText>Directly from you</SText>
                  <SText>Technologies running on our Service </SText>
                  <SText>Other users of our Service</SText>
                  <SText>Affiliated companies within our corporate group</SText>
                  <SText>Our service providers</SText>
                </TD>
                <TD>
                  <SText>
                    To allow you to participate in the features we offer on the
                    Service
                  </SText>
                  <SText>To tailor our communications to you</SText>
                  <SText>Marketing and advertising</SText>
                  <SText>
                    Customer service, auditing, research and analysis,
                    maintaining and improving the Service, and other internal
                    business purposes
                  </SText>
                  <SText>
                    To detect and prevent fraud against you and/or us{" "}
                  </SText>
                </TD>
                <TD>
                  <SText>Affiliated companies within our corporate group</SText>
                  <SText>
                    Service providers that help us communicate with you, provide
                    analytics, perform marketing or advertising services, or
                    otherwise help run our business
                  </SText>
                  <SText>Third-party services, such as social networks</SText>
                </TD>
              </tr>

              <tr>
                <TD>
                  <SText>
                    <strong>
                      Characteristics of protected classifications
                    </strong>
                    , such as age, racial or ethnic identification, and gender.
                    We also collect information about languages you speak
                  </SText>
                </TD>
                <TD>
                  <SText>
                    Directly from you if you choose to provide it to us
                  </SText>
                </TD>
                <TD>
                  <SText>Promoting a diverse community on our Service</SText>
                  <SText>
                    Customer service, auditing, research and analysis,
                    maintaining and improving the Service, and other internal
                    business purposes
                  </SText>
                </TD>
                <TD>
                  <SText>
                    Shared only in an aggregated or deidentified form
                  </SText>
                </TD>
              </tr>

              <tr>
                <TD>
                  <strong>
                    Internet or other electronic network activity information
                  </strong>
                  , including, but not limited to, browsing history, search
                  history, and information regarding your interactions with our
                  Service
                </TD>
                <TD>Technologies running on our Service</TD>
                <TD>
                  To allow you to participate in the features we offer on the
                  Service To provide you with a better experience on the Service
                  Marketing and advertising Customer service, auditing, research
                  and analysis, maintaining and improving the Service, and other
                  internal business purposes To detect and prevent fraud against
                  you and/or us
                </TD>
                <TD>
                  Affiliated companies within our corporate group Service
                  providers that help us communicate with you, provide
                  analytics, perform marketing or advertising services, or
                  otherwise help run our business Third-party services, such as
                  social networks
                </TD>
              </tr>

              <tr>
                <TD>
                  <SText>
                    <strong>Geolocation data</strong> based on information you
                    provide directly or inferred from your IP address or device
                    location
                  </SText>
                </TD>
                <TD>
                  <SText>Directly from you</SText>
                  <SText>Technologies running on our Service </SText>
                  <SText>Other users of our Service</SText>
                  <SText>Affiliated companies within our corporate group</SText>
                  <SText>Our service providers</SText>
                </TD>
                <TD>
                  <SText>
                    To allow you to participate in the features we offer on the
                    Service
                  </SText>
                  <SText>
                    Customer service, auditing, research and analysis,
                    maintaining and improving the Service, and other internal
                    business purposes
                  </SText>
                  <SText>
                    To detect and prevent fraud against you and/or us
                  </SText>
                </TD>
                <TD>
                  <SText>Affiliated companies within our corporate group</SText>
                  <SText>
                    Service providers that help us communicate with you, provide
                    analytics, perform marketing or advertising services, or
                    otherwise help run our business
                  </SText>
                  <SText>Third-party services, such as social networks</SText>
                </TD>
              </tr>

              <tr>
                <TD>
                  <SText>
                    <strong>
                      Audio, electronic, visual, thermal, olfactory, or similar
                      information
                    </strong>
                    , including audio data collected when you speak with us by
                    phone.
                  </SText>
                </TD>
                <TD>
                  <SText>Directly from you</SText>
                </TD>
                <TD>
                  <SText>
                    Customer service, auditing, research and analysis,
                    maintaining and improving the Service, and other internal
                    business purposes
                  </SText>
                </TD>
                <TD>
                  <SText>
                    Service providers that help us communicate with you, provide
                    analytics, perform marketing or advertising services, or
                    otherwise help run our business
                  </SText>
                </TD>
              </tr>

              <tr>
                <TD>
                  <SText>
                    <strong>Inferences</strong> drawn from any of the other
                    personal information we collect about you that reflect your
                    preferences, characteristics, trends, or attitudes
                  </SText>
                </TD>
                <TD>
                  <SText>
                    Derived from any other category of personal information we
                    collect about you
                  </SText>
                </TD>
                <TD>
                  <SText>
                    To provide you with a better experience on the Service
                  </SText>
                  <SText>
                    Customer service, auditing, research and analysis,
                    maintaining and improving the Service, and other internal
                    business purposes
                  </SText>
                  <SText>Marketing and advertising</SText>
                  <SText>
                    To detect and prevent fraud against you and/or us
                  </SText>
                </TD>
                <TD>
                  <SText>Affiliated companies within our corporate group</SText>
                  <SText>
                    Service providers that help us communicate with you, provide
                    analytics, perform marketing or advertising services, or
                    otherwise help run our business
                  </SText>
                  <SText>Third-party services, such as social networks</SText>
                </TD>
              </tr>
            </tbody>
          </Table>

          <SText>
            We may also collect some personal information about you that does
            not directly fit within one of the above categories. For example, we
            collect the contents of communications you send to us by phone,
            email, text, online chat, etc. We use the information included in
            your communications to communicate with you about our Service,
            updates to our terms and policies, and to obtain your feedback; to
            provide customer service and support; to improve our Service and
            marketing; and to detect and prevent fraud against you and/or us.
          </SText>
          <SText>
            We may use and share information for any purpose if it is publicly
            available, de-identified, or aggregated with other information so
            that it can no longer identify you.
          </SText>
        </Section>

        <Section>
          <H2>Technologies That Automatically Collect Information About You</H2>
          As shown in the chart above, we collect your personal information
          through various means, including through technologies that
          automatically collect information about you when you use our Service.
          <SText>
            <strong>Cookies and Tracking Technologies.</strong> We use cookies
            and other tracking technologies (such as web beacons) to collect and
            store information about your interactions with our website,
            including information about your browsing behavior. Our website also
            supports third-party cookies placed by our service providers.
          </SText>
          <SText>
            Cookies are small files that a website transfers to your device
            through a web browser that enable the site&apos;s or a third
            party&apos;s systems to recognize your device and capture and
            remember certain information about your device and browsing
            activity. Web beacons (also known as tracking pixels) are tiny
            graphics embedded invisibly on a webpage or in an email that may be
            used to deliver or communicate with cookies, to count users who have
            visited certain pages, and to understand usage patterns. In general,
            our website uses cookies and other tracking technologies as follows:
          </SText>
          <UL>
            <LI>
              Where necessary to run our website, for example by allowing you to
              log in and access basic site features
            </LI>
            <LI>
              To optimize the functionality of our website, including by
              personalizing content for you, greeting you by name and
              remembering your preferences (e.g., your choice of language)
            </LI>
            <LI>
              For analytics purposes—for example, to help us understand how our
              website is used, and to identify the pages and features that most
              interest you. More information on website analytics is provided
              below.
            </LI>
            <LI>
              To determine if our email messages have been opened and acted
              upon.
            </LI>
          </UL>
          <SText>
            Cookies vary in how long they last. &#8220;Session cookies&#8221;
            terminate shortly after you terminate your internet session.
            &#8220;Persistent cookies&#8221; are stored on your device until a
            set expiration date. We use both session cookies and persistent
            cookies on our website.
          </SText>
          <SText>
            If you do not want to have cookies placed on your device, you should
            set your browser to refuse cookies before accessing our website.
            Please review your browser&apos;s Help menu for instructions, or
            visit www.allaboutcookies.org for more information. Please note
            that, if you disable all cookies, some features of our website may
            not function properly.
          </SText>
          <SText>
            <strong>Analytics.</strong> We may use analytics to better
            understand how users interact with our website. This software may
            collect information such as your IP address, location, browser type
            and language, operating system, access time, duration of visit,
            pages you view within our website, search terms you enter, and other
            actions you take while visiting us, the pages you view immediately
            before and after you access our website, how often you use our
            website, aggregated usage and performance data, application errors,
            and debugging information.
          </SText>
          <SText>
            <strong>Social Features.</strong> Certain features on our Service
            permit you to connect our Service with third-party services, such as
            social networks (&#8220;Social Features&#8221;). Examples of Social
            Features include enabling you to send content between our Service
            and a third-party service, &#8220;liking&#8221; or
            &#8220;sharing&#8221; our content, and otherwise connecting our
            Service to a third-party service (e.g., to pull or push information
            to or from our Service). If you use Social Features, information you
            post or provide access to may be publicly displayed on our Service
            or by the third-party service that you use. Similarly, if you post
            information on a third-party service that references us (e.g., by
            using a hashtag associated with us), your post may be used on or in
            connection with our Service. Also, both we and the third party may
            have access to certain information relating to your use of our
            Service and the third-party service.
          </SText>
          <SText>
            <strong>Our Discord Site.</strong> When you are a part of our
            Discord community, we collect information that you and others share
            on the HUG Discord. Personal information shared by, or about, you on
            Discord is subject to our Privacy Policy and Discord&apos;s Privacy
            Policy.
          </SText>
          <SText>
            <strong>Do Not Track Signals.</strong> Your browser settings may
            allow you to automatically transmit a &#8220;Do Not Track&#8221;
            signal to online services you visit. Note, however, there is no
            consensus among industry participants as to what &#8220;Do Not
            Track&#8221; means in this context. Like many online services, we
            currently do not alter our practices when we receive a &#8220;Do Not
            Track&#8221; signal from a visitor&apos;s browser.
          </SText>
        </Section>

        <Section>
          <H2>How We Share Personal Information</H2>
          <SText>
            The categories of third parties with which we ordinarily share
            personal information are described in the chart above. We may also
            share personal information:{" "}
          </SText>
          <UL>
            <LI>
              In connection with or during negotiation of any reorganization,
              acquisition, merger, sale, joint venture, assignment, transfer or
              other disposition of all or any portion of our business, assets,
              or stock (including in connection with any bankruptcy or similar
              proceeding)
            </LI>
            <LI>To comply with legal process</LI>
            <LI>
              To respond to requests from public and government authorities
            </LI>
            <LI>
              To protect our rights, property, operations, terms and conditions
            </LI>
            <LI>To protect the privacy, safety or property of others</LI>
          </UL>
        </Section>

        <Section>
          <H2>International Visitors</H2>
          <SText>
            Our Service and this Privacy Policy are directed to residents of the
            United States. If you access our Service from outside the United
            States, please be aware that your information may be transferred to,
            stored, and processed in the United States, and that the data
            protection and other laws of the United States may not be as
            comprehensive as those in your country. Your use of the Service or
            provision of any personal information to us constitutes your consent
            to the transfer to and from, processing, usage, sharing, and storage
            of your information in the US as set forth in this Privacy Policy.
          </SText>
        </Section>

        <Section>
          <H2>Your Choices About Email Communications From Us</H2>
          <SText>
            You may unsubscribe from our marketing email communications by
            clicking the &#8220;unsubscribe&#8221; link found in every
            commercial email we send, or by sending a request to unsubscribe to
            unsubscribe@thehug.xyz. Unsubscribing from our marketing
            communications will not affect the level or quality of service we
            provide to you.
          </SText>
        </Section>

        <Section>
          <H2>Security And Passwords</H2>
          <SText>
            We have implemented appropriate and commercially reasonable
            physical, technical, and administrative safeguards to help prevent
            unauthorized access to, use of, and disclosure of, your personal
            information. However, there is no perfect security, and we cannot
            guarantee your personal information will always remain secure. You
            are responsible for maintaining the secrecy of any credentials used
            to access your account with us. If you suspect any unauthorized
            activity associated with your account, please report that activity
            to us immediately.
          </SText>
        </Section>

        <Section>
          <H2>Age Restrictions</H2>
          <SText>
            The Service is intended for adults not less than 16 years of age. If
            you are under 16 years of age, you are not permitted to use the
            Service and should not send any personal information to us.{" "}
          </SText>
        </Section>

        <Section>
          <H2>Changes To This Policy</H2>
          <SText>
            By using our Service, you consent to the terms of this Privacy
            Policy and our Terms of Use. We may change our Privacy Policy from
            time to time. Your use of our Service indicates your consent to the
            privacy policy posted at the time of use.{" "}
          </SText>
        </Section>

        <Section>
          <H2>How To Contact Us</H2>
          <SText>
            If you have any questions about this Privacy Policy, please contact
            by email at legal@assemblestream.com, by mail to Assemble Stream Inc
            ATTN: Legal, 1178 Broadway 3rd Flr #1312, New York NY 10001, or by
            phone at (201) 834-6102.
          </SText>
          <SText>
            This Privacy Policy is designed to be accessible to everyone, but if
            you have any difficulties accessing it in the format in which it is
            presented, please let us know.
          </SText>
        </Section>
      </SiteContainer>
    </Main>
  );
}

const Main = styled.main({}, ({ theme }) => ({
  background: theme.gradients.radial2,
}));

const Section = styled.section({}, ({ theme }) => ({
  padding: "2rem 0",
}));

const Header = styled.header({
  padding: "0 0 4rem",
  position: "relative",
  textAlign: "center",
});

const SText = styled(Text)(({ theme }) => ({}));

const UL = styled.ul({}, ({ theme }) => ({}));

const LI = styled.li({}, ({ theme }) => ({}));

const TD = styled.td({}, ({ theme }) => ({
  verticalAlign: "top",
}));

const global = (theme: Theme) => ({
  "html,body": {
    background: theme.colors.yellow,
  },
});

export { PrivacyPolicy };
